import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories, articles } from '@/mocks/moguvideo';
import type { Article } from '@/mocks/moguvideo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [activeCategory, setActiveCategory] = useState(currentCategory.slug);

  useEffect(() => {
    if (slug) {
      const cat = categories.find((c) => c.slug === slug);
      if (cat) {
        setActiveCategory(cat.slug);
      }
    }
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(articles.filter((a) => a.category === activeCategory));
  }, [activeCategory]);

  const handleCategoryChange = useCallback((catSlug: string) => {
    setActiveCategory(catSlug);
  }, []);

  const currentCatData = categories.find((c) => c.slug === activeCategory) || categories[0];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCatData.name} - 蘑菇视频`,
    description: currentCatData.description,
    url: `https://lygshjx.com.cn/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇视频',
      url: 'https://lygshjx.com.cn/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCatData.name} - 蘑菇视频`}
        description={currentCatData.description}
        keywords={`蘑菇视频${currentCatData.name},${currentCatData.name}在线观看,${currentCatData.name}高清播放,蘑菇视频官网`}
        canonicalUrl={`https://lygshjx.com.cn/category/${activeCategory}`}
        ogType="website"
        schemaData={pageSchema}
        pageType="webpage"
      />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: currentCatData.name },
          ]}
        />

        <main className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {currentCatData.name}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">
                {currentCatData.description}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="mb-10 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max px-1 py-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={(e) => {
                      if (cat.slug === activeCategory) return;
                      handleCategoryChange(cat.slug);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Articles Grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all group cursor-pointer"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={`${article.title} - 蘑菇视频`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-primary-500/90 text-background-50 text-xs font-medium rounded-full">
                          {currentCatData.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                        <span>{article.publishDate}</span>
                        <span>·</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h2 className="font-heading font-semibold text-foreground-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无内容，请查看其他分类</p>
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
}